import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, ShieldCheck, FileText, Phone, EnvelopeSimple, SealCheck } from "@phosphor-icons/react";
import client from "../../lib/api";

const STATUS_TONE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  PENDING: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  DOT_ONLY: "bg-sky-500/15 text-sky-200 border-sky-500/40",
  NOT_FOUND: "bg-white/5 text-white/50 border-white/15",
  ERROR: "bg-white/5 text-white/50 border-white/15",
};
const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export function PublicPacketSection({ slug }) {
  const [pkt, setPkt] = useState(null);
  useEffect(() => {
    let alive = true;
    client.get(`/public/carriers/${slug}/packet`).then(({ data }) => alive && setPkt(data)).catch(() => {});
    return () => { alive = false; };
  }, [slug]);
  if (!pkt) return null;
  const c = pkt.company || {};
  const st = pkt.mc_status;
  return (
    <section className="card-surface p-6 sm:p-8 mb-8 border border-[#FF5A1F]/30" data-testid="public-packet-section">
      <div className="overline text-[#FF5A1F] mb-3 flex items-center gap-1.5"><FileText size={11} weight="fill" /> Carrier Packet · Broker Setup</div>
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {c.mc_number && <span className="font-mono text-sm text-white bg-white/5 border border-white/10 rounded-md px-2 py-0.5" data-testid="public-packet-mc">{c.mc_number}</span>}
            {c.dot_number && <span className="font-mono text-sm text-white bg-white/5 border border-white/10 rounded-md px-2 py-0.5" data-testid="public-packet-dot">USDOT {c.dot_number}</span>}
            {st && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-0.5 ${STATUS_TONE[st.status] || STATUS_TONE.ERROR}`} data-testid="public-packet-mc-status">
                <ShieldCheck size={12} weight="fill" /> {st.label}{st.checked_at ? ` · FMCSA ${new Date(st.checked_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
              </span>
            )}
            {pkt.brokercheck_url && (
              <Link to={pkt.brokercheck_url} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider border border-[#FF5A1F]/50 text-[#FF5A1F] rounded-full px-2.5 py-0.5 hover:bg-[#FF5A1F]/10 transition-colors" data-testid="public-packet-brokercheck">
                <SealCheck size={12} weight="fill" /> Verify on BrokerCheck
              </Link>
            )}
          </div>
          {c.legal_name && <div className="text-white font-semibold" data-testid="public-packet-legal-name">{c.legal_name}</div>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-400 mt-1" data-testid="public-packet-contact">
            {c.contact_name && <span>{c.contact_name}</span>}
            {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone size={13} /> {c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-white"><EnvelopeSimple size={13} /> {c.email}</a>}
          </div>
          <ul className="mt-4 space-y-1.5" data-testid="public-packet-docs">
            {pkt.docs.map((d, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-2 text-sm">
                <FileText size={14} className="text-neutral-500 shrink-0" />
                <span className="text-neutral-400">{d.tag_label}</span>
                <span className="text-white">{d.label}</span>
                <span className="text-neutral-600 text-xs">{kb(d.size_bytes)}</span>
                {d.tag === "coi" && d.expires_on && (
                  <span className={`text-xs ${d.expiry_state === "expired" ? "text-red-400" : d.expiry_state === "expiring_soon" ? "text-amber-300" : "text-emerald-400"}`} data-testid={`public-packet-coi-valid-${i}`}>
                    {d.expiry_state === "expired" ? `expired ${fmtDate(d.expires_on)}` : `valid through ${fmtDate(d.expires_on)}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="shrink-0 lg:w-64">
          {pkt.download_url ? (
            <a href={pkt.download_url} className="btn-primary rounded-full px-5 py-3 inline-flex items-center justify-center gap-2 text-sm w-full" data-testid="public-packet-download">
              <DownloadSimple size={16} weight="bold" /> Download all files (ZIP)
            </a>
          ) : (
            <div className="text-xs text-neutral-500" data-testid="public-packet-empty">Documents are being updated — check back shortly.</div>
          )}
          <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">Always the current documents on file — if the COI renews you'll get the new certificate here, no email needed.</p>
        </div>
      </div>
    </section>
  );
}
